import styled from '@emotion/styled'
import {CanvasWidget} from '@projectstorm/react-canvas-core'
import {useAppSelector} from '../../../store'
import {selectDarkMode} from '../../../store/slices/themeSlice'
import {Application} from '../Application/Application'

export interface BodyWidgetProps {
  app: Application
}

export const BodyWidget = ({app}: BodyWidgetProps) => {
  const isDark = useAppSelector(selectDarkMode)
  const clearInvalidLinks = () => {
    const diagramModel = app.getActiveDiagram()
    const engine = app.getDiagramEngine()

    if (!diagramModel || !engine) return

    const links = diagramModel.getLinks()
    links.forEach(link => {
      const sourcePortId = link.getSourcePort()?.getID()
      const targetPortId = link.getTargetPort()?.getID()

      const hasSourceTargetPortIds = !!sourcePortId && !!targetPortId
      const hasSourceTargetDifferentPorts = sourcePortId !== targetPortId

      const isValidLink = hasSourceTargetPortIds && hasSourceTargetDifferentPorts
      if (isValidLink) {
        return
      }

      diagramModel.removeLink(link)
    })

    engine.repaintCanvas()
  }

  const zoomIn = () => {
    const model = app.getActiveDiagram()
    const engine = app.getDiagramEngine()
    if (!model || !engine) return
    model.setZoomLevel(model.getZoomLevel() + 10)
    engine.repaintCanvas()
  }

  const zoomOut = () => {
    const model = app.getActiveDiagram()
    const engine = app.getDiagramEngine()
    if (!model || !engine) return
    model.setZoomLevel(Math.max(10, model.getZoomLevel() - 10))
    engine.repaintCanvas()
  }

  const zoomToFit = () => {
    app.zoomToFitSteps()
  }

  return (
    <div
      data-testid="canvas-container-wrapper"
      data-cy="canvas-container-wrapper"
      className="relative flex h-full flex-grow flex-col"
      onMouseUp={clearInvalidLinks}
      onMouseLeave={clearInvalidLinks}
    >
      <S.Container color={isDark ? '#3a3a3a' : '#DFEAF9'} bgColor={isDark ? '#1f1f1f' : '#fbfcfe'}>
        <CanvasWidget engine={app.getDiagramEngine()} />
      </S.Container>
      <div className="absolute bottom-3 left-3 flex flex-col gap-1">
        <button
          onClick={zoomIn}
          title="Zoom in"
          className="flex h-7 w-7 items-center justify-center rounded border border-border-dashed bg-button-icon text-lg font-bold text-button-icon-text hover:bg-button-primary hover:text-white dark:text-link"
        >
          +
        </button>
        <button
          onClick={zoomOut}
          title="Zoom out"
          className="flex h-7 w-7 items-center justify-center rounded border border-border-dashed bg-button-icon text-lg font-bold text-button-icon-text hover:bg-button-primary hover:text-white dark:text-link"
        >
          −
        </button>
        <button
          onClick={zoomToFit}
          title="Zoom to fit"
          className="flex h-7 w-7 items-center justify-center rounded border border-border-dashed bg-button-icon text-xs text-button-icon-text hover:bg-button-primary hover:text-white dark:text-link"
        >
          ⊡
        </button>
      </div>
    </div>
  )
}

const S = {
  Container: styled.div<{color: string; bgColor: string}>`
    height: 100%;
    background-color: ${p => p.bgColor};
    background-size: 25px 25px;
    display: flex;
    > * {
      height: 100%;
      min-height: 100%;
      width: 100%;
    }
    background-image: linear-gradient(
        0deg,
        transparent 24%,
        ${p => p.color} 25%,
        ${p => p.color} 26%,
        transparent 27%,
        transparent 74%,
        ${p => p.color} 75%,
        ${p => p.color} 76%,
        transparent 77%,
        transparent
      ),
      linear-gradient(
        90deg,
        transparent 24%,
        ${p => p.color} 25%,
        ${p => p.color} 26%,
        transparent 27%,
        transparent 74%,
        ${p => p.color} 75%,
        ${p => p.color} 76%,
        transparent 77%,
        transparent
      );
  `,
}
